import { Fragment, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";

import useTransferWorkspaceOwnership from "../../api/api-hooks/workspaces/useTransferWorkspaceOwnership";
import InviteUsers from "../../views/cloud/CreateCloudView/CreateCloudComponents/InviteUsers";
import useDeleteWorkspaceUser from "../../api/api-hooks/workspaces/useDeleteWorkspaceUser";
import useModifyWorkspaceUser from "../../api/api-hooks/workspaces/useModifyWorkspaceUser";
import useAddWorkspaceUser from "../../api/api-hooks/workspaces/useAddWorkspaceUser";
import ManageUserPermissionsDrawer from "../Drawers/ManageUserPermissionsDrawer";
import useTenantUsers from "../../api/api-hooks/tenantUsers/useTenantUsers";
import useWorkspacePermissions from "../../hooks/useWorkspacePermissions";
import useInviteUser from "../../api/api-hooks/tenantUsers/useInviteUser";
import TransferOwnershipDrawer from "../Drawers/TransferOwnershipDrawer";
import useWorkspace from "../../api/api-hooks/workspaces/useWorkspace";
import { getUserPermissionID } from "../../utils/UserUtils";
import useCloud from "../../api/api-hooks/cloud/useCloud";
import useUser from "../../api/api-hooks/auth/useUser";
import { newToastEvent } from "../../utils/ToastUtil";
import { Person } from "../../types/GlobalInterfaces";
import AppPageHeader from "../Headers/AppPageHeader";
import { AppConfirm } from "../../utils/PopupUtil";
import AppSpinner from "../../elements/AppSpinner";
import MemberOptions from "./MemberOptions";
import ListItem from "../CRM/ListItem";

const ManageMembers = () => {
  const { users, usersLoading } = useTenantUsers();
  const { workspace, workspaceData, workspaceMembers } = useWorkspace();
  const { user, wPermissions: permissions } = useUser();
  const memberOptionsRef = useRef<any>();
  const { tenantID } = useParams();
  const { transferOwnership, transferringOwnership, transferOwnershipError } =
    useTransferWorkspaceOwnership();
  const { addWorkspaceUser, addingWorkspaceUser, addWorkspaceUserError } =
    useAddWorkspaceUser();
  const { modifyUser, modifyingUser, modifyUserError } =
    useModifyWorkspaceUser();
  const { deleteWorkspaceUser } = useDeleteWorkspaceUser();
  const { wPermissions } = useWorkspacePermissions();
  const [transferUser, setTransferUser] = useState<any>(null);
  const [selectedEmail, setSelectedEmail] = useState<any>("");
  const [selectedPermissions, setSelectedPermissions] = useState<any>(null);
  const [manageUser, setManageUser] = useState(null);
  const navigate = useNavigate();
  const { inviteUser } = useInviteUser();
  const { cloudPermissions, cloud } = useCloud(tenantID!);

  const handleGetPermissionID = useCallback(
    (user: Person) => {
      //@ts-ignore
      return getUserPermissionID(user, "Workspaces", workspace.id, tenantID);
    },
    [workspace.id]
  );

  const inviteMember = () => {
    const foundUser = users.find(
      (u: Person) => u.PrimaryContactEmail === selectedEmail
    );

    if (!foundUser && permissions) {
      const workspacePermissions = permissions.find(
        (p) => p.id === selectedPermissions
      );
      const cloudPermission = cloudPermissions.find((p) => {
        const pName = p.Name.split(" ")[1];
        return workspacePermissions.Name.includes(pName);
      });

      inviteUser({
        email: selectedEmail,
        permission: cloudPermission!.id!,
        supportTargetCloudID: undefined, // This is NOT a support request.
        workspaceInfo: {
          workspaceID: workspace.id!,
          workspaceProfileID: selectedPermissions,
        },
        onSuccess: () => {
          newToastEvent(
            "success",
            `Successfully added member to the ${workspace?.Name} workspace.`
          );

          setSelectedEmail(null);
          setSelectedPermissions(null);
        },
      });

      return;
    }

    addWorkspaceUser({
      userID: foundUser?.id,
      permissionProfile: selectedPermissions,
      onSuccess: () => {
        newToastEvent(
          "success",
          `Successfully added member to the ${workspace?.Name} workspace.`
        );

        setSelectedEmail("");
        setSelectedPermissions(null);
      },
    });
  };

  const removeMember = (memberID: string, name: string) => {
    AppConfirm({
      title:
        memberID === user?.id
          ? "Leave The Workspace?"
          : "Remove " + name + " From Workspace?",
      html:
        memberID === user?.id
          ? "Are you sure you want to leave the " +
            workspace?.Name +
            " workspace?"
          : `Are you sure you want to remove ${name} from the ${workspace?.Name} workspace?`,
      confirmButtonText: memberID === user?.id ? "Leave" : "Remove",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteWorkspaceUser({
          userID: memberID,
          onSuccess: () => {
            if (memberID === user?.id) {
              newToastEvent(
                "success",
                <>
                  <div>Successfully left the {workspace?.Name} workspace.</div>
                </>
              );

              navigate("/clouds/" + tenantID + "/workspaces");
            } else {
              newToastEvent(
                "success",
                <>
                  <div>
                    Successfully removed {name} from the {workspace?.Name}{" "}
                    workspace.
                  </div>
                </>
              );
            }

            setSelectedEmail(null);
            setSelectedPermissions(null);
          },
        });
      }
    });
  };

  const LoadingManageMembers = () => (
    <div>
      <AppSpinner style={{ height: 30, width: 30 }} />
    </div>
  );

  if (usersLoading) return <LoadingManageMembers />;

  return (
    <>
      <div className="mb-3">
        <AppPageHeader
          title="Members"
          description="Invite and manage members permissions"
          size="sm"
          titleBreakpoints={{ xs: "auto" }}
          actionsBreakpoints={{ xs: 12, sm: 12, md: "auto" }}
          customContent={
            <div style={{ width: "calc(100vw - 310px)" }} className="mt-2">
              <InviteUsers
                email={selectedEmail}
                setEmail={setSelectedEmail}
                permissionID={selectedPermissions}
                setPermissionID={setSelectedPermissions}
                userFilter={(person) =>
                  !workspaceData.memberIds.includes(person?.id)
                }
                onAdd={inviteMember}
                permissions={permissions ?? []}
                permissionsScope="Workspace"
                submitting={addingWorkspaceUser}
                useExistingUsers={true}
                members={workspaceMembers}
                error={addWorkspaceUserError}
              />
            </div>
          }
        />
        <hr className="my-3" />
      </div>

      <div>
        {workspaceMembers?.map((member: any, i: number) => {
          const isOwner = member?.PersonID === workspace.Owner;
          const permissions = member.Permissions.WorkspaceProfile;
          const isCurrentUser = member?.PersonID === user?.id;

          return (
            <Fragment key={member.PersonID}>
              <ListItem
                title={`${user.id === member.PersonID ? "(Me) " : ""} ${
                  member?.Data?.Name || member?.Data?.PrimaryContactEmail
                }`}
                subTitle={
                  !member?.Data?.Name
                    ? "Pending Invitation"
                    : member?.Data?.PrimaryContactEmail
                }
                icon={isOwner ? "mdi-crown" : "mdi-account"}
                badgeTitle={permissions?.Name}
                badgeColor={isOwner ? "secondary" : "light"}
                options={
                  <MemberOptions
                    member={member}
                    isOwner={isOwner}
                    isCurrentUser={isCurrentUser}
                    wPermissions={wPermissions}
                    removeMember={removeMember}
                    setTransferUser={setTransferUser}
                    setManageUser={setManageUser}
                    closeWhisper={() => memberOptionsRef.current?.close()}
                  />
                }
              />
              {workspaceMembers?.length - 1 !== i && <hr className="my-0"></hr>}
            </Fragment>
          );
        })}
      </div>
      <ManageUserPermissionsDrawer
        permissions={permissions}
        open={manageUser !== null}
        onClose={() => setManageUser(null)}
        selectedUser={manageUser}
        getPermissionID={handleGetPermissionID}
        type="Workspace"
        updatePermissions={modifyUser}
        updatingPermissions={modifyingUser}
        updatePermissionsError={modifyUserError}
        supportTargetCloudID={undefined} // This is NOT a support request.
      />
      <TransferOwnershipDrawer
        permissions={permissions}
        open={transferUser !== null}
        onClose={() => setTransferUser(null)}
        newUser={transferUser}
        type="Workspace"
        assignOwnership={transferOwnership}
        assigningOwnership={transferringOwnership}
        assignOwnershipError={transferOwnershipError}
      />
    </>
  );
};

export default ManageMembers;
